import { css, cva } from '@styled-system/css';
import type { FC, ReactNode } from 'react';

interface ChipToggleProps {
  selected: boolean;
  onChange: (next: boolean) => void;
  /** Optional colored dot displayed before the label (used for color filters). */
  dot?: string;
  /** Optional icon displayed before the label (e.g. a platform logo). */
  icon?: ReactNode;
  /** Optional icon displayed after the label (e.g. a lock). */
  trailingIcon?: ReactNode;
  disabled?: boolean;
  /**
   * Rendu désactivé SANS l'attribut natif `disabled` (`aria-disabled="true"`) : le bouton reçoit
   * encore survol, focus et clic — pour une puce verrouillée qui affiche une infobulle et mène
   * ailleurs au clic (cf `VestiaireLockedChip`).
   */
  ariaDisabled?: boolean;
  /** Id d'un texte qui décrit la puce (ex. motif d'un verrou, lu par les lecteurs d'écran). */
  ariaDescribedBy?: string;
  children: ReactNode;
  className?: string;
}

const chip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3',
    paddingY: '3',
    paddingX: '5',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderRadius: 'md',
    fontFamily: 'sans',
    fontSize: 'body',
    fontWeight: 'regular',
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
    bg: 'surface.subtle',
    borderColor: 'border.default',
    color: 'text.primary',
    _hover: { borderColor: 'border.strong', bg: 'surface.sunken' },
    _disabled: { opacity: 0.5, cursor: 'not-allowed' },
    // Même rendu que `disabled`, survol neutralisé
    '&[aria-disabled=true]': {
      opacity: 0.5,
      cursor: 'not-allowed',
      _hover: { borderColor: 'border.default', bg: 'surface.subtle' },
    },
  },
  variants: {
    selected: {
      true: {
        bg: 'brand.primarySoft',
        borderColor: 'brand.primary',
        color: 'text.brand',
        _hover: { borderColor: 'brand.primary', bg: 'brand.primarySoft' },
      },
      false: {},
    },
  },
});

const dotStyle = css({
  width: '10px',
  height: '10px',
  borderRadius: 'full',
  flexShrink: 0,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
});

export const ChipToggle: FC<ChipToggleProps> = ({
  selected,
  onChange,
  dot,
  icon,
  trailingIcon,
  disabled,
  ariaDisabled,
  ariaDescribedBy,
  children,
  className,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={selected}
    aria-disabled={ariaDisabled ? true : undefined}
    aria-describedby={ariaDescribedBy}
    disabled={disabled}
    onClick={() => onChange(!selected)}
    className={`${chip({ selected })} ${className ?? ''}`}
  >
    {dot != null && <span className={dotStyle} style={{ background: dot }} />}
    {icon}
    <span>{children}</span>
    {trailingIcon}
  </button>
);
